import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import ExportButton from '../components/ExportButton';
import PassageTable from '../components/PassageTable';
import LoadingSpinner from '../components/LoadingSpinner';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';

// Stili CSS in linea
const styles = {
    container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
    title: { borderBottom: '2px solid var(--border-color)', paddingBottom: '10px', color: 'var(--text-primary)' },
    filterBar: {
        display: 'flex',
        gap: '1.5rem',
        alignItems: 'flex-end',
        margin: '2rem 0',
        flexWrap: 'wrap',
        padding: '1.5rem',
        background: 'var(--card-background)',
        borderRadius: '8px',
        boxShadow: 'var(--shadow-sm)'
    },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }
};

const PassageReport = () => {
    // 1. Recupera l'utente loggato dal contesto
    const { user } = useAuth();

    const [filters, setFilters] = useState({
        location: '',
        passageCounterID: '',
        startDate: null,
        endDate: null,
    });
    const [filterOptions, setFilterOptions] = useState({
        locations: [],
        interestPoints: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const queryClient = useQueryClient();

    // 2. Logica "Solo Dashboard": Imposta e blocca il filtro in base all'ID utente
    useEffect(() => {
        if (user) {
            // Nota: L'ID viene convertito in numero per sicurezza, in caso arrivi come stringa
            const userId = Number(user.id);
            let restrictedPark = null;

            if (userId === 26) {
                restrictedPark = 'Alpi Cozie';
            } else if (userId === 30) {
                // Assumo che per l'utente "granparadiso@..." il parco sia Gran Paradiso
                restrictedPark = 'Gran Paradiso';
            }

            // Se l'utente ha una restrizione, forza il filtro location
            if (restrictedPark) {
                setFilters(prev => ({
                    ...prev,
                    location: restrictedPark,
                    passageCounterID: '' // Resetta i punti di interesse se cambia il parco forzatamente
                }));
            }
        }
    }, [user]);

    useEffect(() => {
        const fetchOptions = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get('/api/backoffice/passage-filter-options', { withCredentials: true });
                setFilterOptions(data);
                setError(null);
            } catch (err) {
                console.error("Errore nel caricare le opzioni dei filtri", err);
                setError("Impossibile caricare le opzioni di filtro.");
            } finally {
                setLoading(false);
            }
        };
        fetchOptions();
    }, []);

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            if (key === 'location') {
                newFilters.passageCounterID = '';
            }
            return newFilters;
        });
    };

    const handleRefresh = () => {
        queryClient.invalidateQueries(['passages']);
    };

    const availableInterestPoints = useMemo(() => {
        if (!filters.location) {
            return filterOptions.interestPoints;
        }
        return filterOptions.interestPoints.filter(ip => ip.location === filters.location);
    }, [filters.location, filterOptions.interestPoints]);

    // 3. Calcola se il filtro location deve essere disabilitato (read-only)
    // Usa Number() per garantire il confronto corretto anche se user.id fosse stringa
    const isLocationLocked = user && (Number(user.id) === 26 || Number(user.id) === 30);

    if (error) {
        return <div style={styles.container}><p style={{ color: 'red' }}>{error}</p></div>;
    }

    return (
        <div style={styles.container}>
            <h1 style={styles.title}>Report Passaggi</h1>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Filtra e visualizza i passaggi registrati. Clicca sulle intestazioni della tabella per ordinare i dati.
            </p>

            {loading ? (
                <LoadingSpinner />
            ) : (
                <div style={styles.filterBar}>
                    <div style={styles.filterGroup}>
                        <label style={styles.label} htmlFor="filter-location">Parco</label>
                        <select
                            id="filter-location"
                            value={filters.location}
                            onChange={(e) => handleFilterChange('location', e.target.value)}
                            // 4. Disabilita la select se l'utente è ristretto
                            disabled={isLocationLocked}
                            style={isLocationLocked ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', color: '#6c757d' } : {}}
                        >
                            {/* Nascondi l'opzione "Tutti" se il filtro è bloccato */}
                            {!isLocationLocked && <option value="">Tutti</option>}

                            {filterOptions.locations.map(loc => (
                                <option key={loc} value={loc}>{loc}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.label} htmlFor="filter-interest-point">Punto di Interesse</label>
                        <select
                            id="filter-interest-point"
                            value={filters.passageCounterID}
                            onChange={(e) => handleFilterChange('passageCounterID', e.target.value)}
                        >
                            <option value="">Tutti</option>
                            {availableInterestPoints.map(ip => (
                                <option key={ip.id} value={ip.id}>
                                    {ip.name} (Contapassaggi: {ip.counterId})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Data Inizio</label>
                        <DatePicker
                            selected={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            isClearable
                            placeholderText="dd/mm/yyyy"
                            dateFormat="dd/MM/yyyy"
                            className="date-picker-input"
                        />
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Data Fine</label>
                        <DatePicker
                            selected={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            isClearable
                            placeholderText="dd/mm/yyyy"
                            dateFormat="dd/MM/yyyy"
                            className="date-picker-input"
                        />
                    </div>

                    <button onClick={handleRefresh} className="button button-outline" style={{ alignSelf: 'flex-end' }}>
                        Aggiorna
                    </button>

                    <ExportButton currentFilters={filters} />
                </div>
            )}

            <div className="table-container">
                <PassageTable filters={filters} />
            </div>
        </div>
    );
};

export default PassageReport;